import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { BiData, BiContextProvidedValue } from '@wix/da-bi/pkg/events';
import { ExclusiveCategory, getExclusiveCategoryByNecoid } from '../../../../types/exclusives';
import { useRestrictToLoggedInAndVerified } from '../../../../utils/hooks/useRestrictToLoggedInAndVerified';
import { pushAdoptsBuyerModal, pushAdoptsSellerModal } from '../../../../Modals/redux/actionCreators';
import useAdoptableState from '../AdoptableInfo/useAdoptableState';
// TODO: Move these useXAction hooks somewhere central. They're used in multiple places that aren't the sidebar
/**
 * Returns a callback that's to be added to a "Buy it Now" button. Sends the user to checkout.
 *
 * Right now, only adoptables are supported.
 */ export function useBuyNowAction(deviation, biData, foruserid) {
    const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
    const buyNowAction = useCallback(()=>{
        var _deviation_extended_adoptable_fixedPrice, _deviation_extended_adoptable, _deviation_extended;
        var _deviation_extended_adoptable_fixedPrice_subproductId;
        window.location.assign(Url.checkoutLink((_deviation_extended_adoptable_fixedPrice_subproductId = (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : (_deviation_extended_adoptable_fixedPrice = _deviation_extended_adoptable.fixedPrice) === null || _deviation_extended_adoptable_fixedPrice === void 0 ? void 0 : _deviation_extended_adoptable_fixedPrice.subproductId) !== null && _deviation_extended_adoptable_fixedPrice_subproductId !== void 0 ? _deviation_extended_adoptable_fixedPrice_subproductId : 0, foruserid));
    }, [
        deviation,
        foruserid
    ]);
    return [
        restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, buyNowAction),
        BiData(_object_spread({
            evid: 750,
            itemid: deviation.deviationId,
            typeid: deviation.typeId,
            is_gift: 0
        }, biData))
    ];
}
export function useGiftNowAction(deviation, biData, foruserid) {
    const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
    const giftNowAction = useCallback(()=>{
        var _deviation_extended_adoptable_fixedPrice, _deviation_extended_adoptable, _deviation_extended;
        var _deviation_extended_adoptable_fixedPrice_subproductId;
        window.location.assign(Url.checkoutLink((_deviation_extended_adoptable_fixedPrice_subproductId = (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : (_deviation_extended_adoptable_fixedPrice = _deviation_extended_adoptable.fixedPrice) === null || _deviation_extended_adoptable_fixedPrice === void 0 ? void 0 : _deviation_extended_adoptable_fixedPrice.subproductId) !== null && _deviation_extended_adoptable_fixedPrice_subproductId !== void 0 ? _deviation_extended_adoptable_fixedPrice_subproductId : 0, foruserid));
    }, [
        deviation,
        foruserid
    ]);
    return [
        restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, giftNowAction),
        BiData(_object_spread({
            evid: 750,
            itemid: deviation.deviationId,
            typeid: deviation.typeId,
            sectionname: BiContextProvidedValue,
            is_gift: 1
        }, biData))
    ];
}
/**
 * Returns a callback that's to be added to a "Make Offer" button. Triggers an offer modal.
 *
 * Right now, only adoptables are supported.
 */ export function useMakeOfferAction(deviation, biData) {
    const dispatchPushAdoptsBuyerModal = useAction(pushAdoptsBuyerModal);
    const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
    const makeOfferAction = useCallback(()=>{
        dispatchPushAdoptsBuyerModal(deviation, 'offer');
    }, [
        deviation,
        dispatchPushAdoptsBuyerModal
    ]);
    return [
        restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, makeOfferAction),
        BiData(_object_spread({
            evid: 751,
            itemid: deviation.deviationId,
            typeid: deviation.typeId
        }, biData))
    ];
}
export function useManageOfferAction(deviation, biData) {
    const [makeOfferAction] = useMakeOfferAction(deviation, {
        sectionname: ''
    });
    return [
        makeOfferAction,
        BiData(_object_spread({
            evid: 753,
            itemid: deviation.deviationId,
            typeid: deviation.typeId
        }, biData))
    ];
}
export function useViewOffersAction(deviation, biData) {
    const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
    const dispatchPushAdoptsBuyerModal = useAction(pushAdoptsBuyerModal);
    const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
    const viewOffersAction = useCallback(()=>{
        var _deviation_extended_adoptable, _deviation_extended;
        if ((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.isOwner) {
            dispatchPushAdoptsSellerModal(deviation, 'offers');
        } else {
            dispatchPushAdoptsBuyerModal(deviation, 'view-offers');
        }
    }, [
        deviation,
        dispatchPushAdoptsSellerModal,
        dispatchPushAdoptsBuyerModal
    ]);
    return [
        restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, viewOffersAction),
        BiData(_object_spread({
            evid: 746,
            itemid: deviation.deviationId,
            typeid: deviation.typeId
        }, biData))
    ];
}
export function useSellAction(deviation) {
    const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
    const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
    const sellAction = useCallback(()=>{
        dispatchPushAdoptsSellerModal(deviation, 'sell-splash');
    }, [
        deviation,
        dispatchPushAdoptsSellerModal
    ]);
    return [
        restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, sellAction),
        BiData({
            evid: 353,
            deviationid: deviation.deviationId
        })
    ];
}
export function useResellAction(deviation, adoptableState) {
    var _deviation_extended_adoptable, _deviation_extended;
    const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
    const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
    const resellAction = useCallback(()=>{
        dispatchPushAdoptsSellerModal(deviation, 'create');
    }, [
        deviation,
        dispatchPushAdoptsSellerModal
    ]);
    return [
        adoptableState.isResellOptionEnabled && restrictToLoggedInAndVerified(ReferralType.ACTION_BAR, undefined, resellAction),
        BiData({
            evid: 739,
            itemid: deviation.deviationId,
            typeid: deviation.typeId,
            necoid: (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.necoId
        })
    ];
}
/**
 * Will perform either of our standard Sell or Resell actions. Sell action is returned when
 * the exclusive has never been sold. Resell is returned when the exclusive has been sold before.
 */ export function useSellOrResellAction(deviation) {
    const adoptableState = useAdoptableState(deviation);
    const sellActionAndBiData = useSellAction(deviation);
    const resaleActionAndBiData = useResellAction(deviation, adoptableState);
    return adoptableState.saleCount > 0 ? resaleActionAndBiData : sellActionAndBiData;
}
export function useEndSaleAction(deviation, biData) {
    const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
    const endSaleAction = useCallback(()=>{
        dispatchPushAdoptsSellerModal(deviation, 'end-confirm');
    }, [
        dispatchPushAdoptsSellerModal,
        deviation
    ]);
    return [
        endSaleAction,
        BiData(_object_spread({
            evid: 742,
            itemid: deviation.deviationId,
            typeid: deviation.typeId
        }, biData))
    ];
}
export function useExclusiveCategoryTranslation(deviation) {
    var _deviation_extended_adoptable, _deviation_extended;
    const { t } = useTranslation();
    if (!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.necoId)) {
        return t('common.exclusiveCategoryName.unknown');
    }
    switch(getExclusiveCategoryByNecoid(deviation.extended.adoptable.necoId.toString())){
        case ExclusiveCategory.Adoptable:
            return t('common.exclusiveCategoryName.adoptable');
        case ExclusiveCategory.DigitalArt:
            return t('common.exclusiveCategoryName.digitalArt');
        case ExclusiveCategory.GameArt:
            return t('common.exclusiveCategoryName.gameArt');
        case ExclusiveCategory.MixedMedia:
            return t('common.exclusiveCategoryName.mixedMedia');
        case ExclusiveCategory.Photography:
            return t('common.exclusiveCategoryName.photography');
        case ExclusiveCategory.Pinups:
            return t('common.exclusiveCategoryName.pinups');
        case ExclusiveCategory.TraditionalArt:
            return t('common.exclusiveCategoryName.traditionalArt');
        case ExclusiveCategory.ThreeDArt:
            return t('common.exclusiveCategoryName.3DArt');
        default:
            return t('common.exclusiveCategoryName.unknown');
    }
}

//# sourceMappingURL=hooks.js.map